/**
 * Financial Metrics Calculator - Pure functions for calculating financial performance metrics
 *
 * This module provides Level 5 pure functions for calculating key financial metrics
 * used in evaluating bond candidates and their financial performance.
 */

/**
 * Calculates net available revenue after existing debt service obligations
 * @param totalRevenue - Total revenue collected
 * @param existingDebtService - Existing debt service obligations
 * @returns Net revenue available for new bond payments
 * @pure Level 5 calculation - no side effects
 */
export function calculateNetAvailableRevenue(
  totalRevenue: number,
  existingDebtService: number
): number {
  return totalRevenue - existingDebtService;
}

/**
 * Calculates revenue utilization ratio for bond payments
 *
 * ⚠️ DEPRECATED: This function calculates "bond payment utilization of net available revenue"
 * which is inconsistent with DRF-based utilization now used in ResultsEvaluator.
 *
 * New standard (in ResultsEvaluator): (totalRevenue - drfChange) / totalRevenue
 * This function kept for backward compatibility but should not be used for new code.
 *
 * @param totalBondPayments - Total new bond payments (principal + interest)
 * @param netAvailableRevenue - Net revenue available after existing debt
 * @returns Revenue utilization ratio (0-1+ scale, where 1.0 = 100%)
 * @pure Level 5 calculation - no side effects
 */
export function calculateRevenueUtilization(
  totalBondPayments: number,
  netAvailableRevenue: number
): number {
  return netAvailableRevenue > 0 ? totalBondPayments / netAvailableRevenue : 0;
}

/**
 * Calculates interest ratio as percentage of total revenue
 * Lower is better - indicates less revenue consumed by interest costs
 * @param totalInterest - Total interest payments
 * @param totalRevenue - Total revenue collected
 * @returns Interest ratio (0-1 scale, where 0.1 = 10% of revenue)
 * @pure Level 5 calculation - no side effects
 */
export function calculateInterestRatio(totalInterest: number, totalRevenue: number): number {
  return totalRevenue > 0 ? totalInterest / totalRevenue : 0;
}

/**
 * Calculates payment efficiency ratio (principal vs total payments)
 * Higher is better - indicates more spending creates value rather than paying interest
 * Example: 0.7 efficiency means 70% goes to principal, 30% to interest
 * @param totalPrincipal - Total principal payments
 * @param totalInterest - Total interest payments
 * @returns Payment efficiency ratio (0-1 scale, where 0.7 = 70% efficiency)
 * @pure Level 5 calculation - no side effects
 */
export function calculatePaymentEfficiency(totalPrincipal: number, totalInterest: number): number {
  const totalPayments = totalPrincipal + totalInterest;
  return totalPayments > 0 ? totalPrincipal / totalPayments : 0;
}
